import { User } from 'models/user'
import to from 'await-to-js'
import { notifyError, notifySuccess } from './toast'
import qs from 'query-string'
import { Product } from '../models/product'
import { clientInstance } from '../utils/http-client'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { ChainListResponse } from '../models/chains'
import { Deal, DealCreate, DealEdit, DealHistory } from '../models/deals'
import { SalesPipeline } from '../models/SalesPipeline'
import { SalesStage } from '../models/sales_stages'
import { Tag } from '../models/tags'
import getStageColor from '../utils/getStageColor'
import { ChainsSummary, PlacesSummary } from 'models/summaries'
import {
  ContactCreateRequest,
  ContactListResponse,
  ContactsImportResponse,
} from 'models/contacts'
import {
  Category,
  CompanyDistributor,
  CompanyDistributorCreate,
  ContactCompanySearchResult,
  ContactType,
} from 'models/companies'
import {
  IContactCompany,
  IContactCompanyCreateRequest,
  IContactCompanyUpdateRequest,
  IDoorProxy,
  IDoorProxyCreateRequest,
  IDoorProxyEditRequest,
  IDoorProxyListResponse,
  INote,
} from '../models/contact_companies'
import { Notification } from 'models/notifications'
import { PaginatedResponse } from '../models/paginated-response'
import { ProductPrediction } from 'models/deals'
import { DealsPipeline } from 'models/deals_pipeline'
import { Preferences } from 'models/preferences'
import { ActivityListResponse } from 'models/activity'
import { ActivityListResponseV2 } from 'models/activityV2'
import {
  SavedView,
  SavedViewCreate,
  SavedViewIdentifier,
} from 'models/saved_view'
import {
  bulkEditDistributorsParams,
  bulkEditSingleParams,
  bulkEditTagsParams,
} from './apiTypes'
import { Menu } from 'models/menus'
import { useMemo } from 'react'
import { Restaurant } from 'models/restaurant'
import { UniversityChainProxy } from 'models/universities'
import {
  exportK12Districts,
  getK12DistrictsList,
  k12BulkEditDistributors,
  k12BulkEditTags,
  useGetK12District,
  useK12CountiesOptions,
} from './k12api'
import {
  CHAIN_SUMMARY_KEY,
  CHAINS_TABLE_KEY,
  COLLEGE_AND_UNIVERSITY_TABLE_KEY,
  CONTACTS_TABLE_KEY,
  DEAL_TABLE_KEY,
  DOOR_SUMMARY_KEY,
  DOORS_TABLE_KEY,
  K12_TABLE_KEY,
  UGC_TABLE_KEY,
} from 'constants/tableQueryKeys'
import { SuggestionCreate } from 'models/suggestion'
import { queryClient } from './queryClient'

type tagParent = 'products' | 'contacts'

export interface BaseAPICallOptions {
  successMessage?: string
  disableNotifyError?: boolean
  disableNotifyGenericError?: boolean
}

export async function baseAPICall<T>(
  fetcher: () => Promise<AxiosResponse<T>>,
  {
    successMessage,
    disableNotifyError,
    disableNotifyGenericError,
  }: BaseAPICallOptions = {}
) {
  const [err, res] = await to(fetcher())

  const ignoreErrorCodes = ['ERR_NETWORK', 'ERR_CANCELED']

  if (
    err &&
    axios.isAxiosError(err) &&
    !ignoreErrorCodes.includes(err.code ?? '')
  ) {
    if (err.response?.status === 408) {
      notifyError(
        'Sorry, our servers are under heavy load now, please try again.'
      )
    } else {
      if (err?.response?.data.error_message) {
        if (!disableNotifyError) {
          notifyError(err.response.data.error_message)
        }
      } else if (!disableNotifyGenericError) {
        notifyError(err.message)
      }
    }
    throw err
  }

  if (!res) throw err

  if (successMessage) {
    notifySuccess(successMessage)
  }

  return res.data
}

export default function apiService() {
  async function bulkEditTags(
    type:
      | 'doors'
      | 'chains'
      | 'companies'
      | 'companies_from_contacts'
      | 'deals'
      | 'contacts'
      | 'k12'
      | 'universities',
    values: bulkEditTagsParams,
    subCategoryId?: number
  ) {
    const urlMap = {
      doors: 'restaurants/places/bulk/tags/',
      chains: 'restaurants/chains/bulk/tags/',
      companies: `contacts/contactcompanies/bulk/tags/`,
      companies_from_contacts: `contacts/contactcompanies/bulk/tags/from-contacts/`,
      deals: 'contacts/deals/bulk/tags/',
      contacts: 'contacts/bulk/tags/',
      universities: 'universities/bulk/tags/',
      k12: 'k12/districts/bulk/tags/',
    }
    const url = urlMap[type]

    const { params, ...rest } = values

    const queryParams = params ?? {}

    if (type === 'companies') {
      queryParams['sub_category_id'] = subCategoryId
    }

    const [err, res] = await to(
      clientInstance.post(url, rest, {
        params: queryParams,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )

    if (err) {
      notifyError(err.message)
      throw err
    }

    // Invalidate corresponding queries
    const queryKeysMap: Record<string, string[]> = {
      doors: [
        CHAINS_TABLE_KEY,
        DOORS_TABLE_KEY,
        DOOR_SUMMARY_KEY,
        CHAIN_SUMMARY_KEY,
      ],
      chains: [
        CHAINS_TABLE_KEY,
        DOORS_TABLE_KEY,
        DOOR_SUMMARY_KEY,
        CHAIN_SUMMARY_KEY,
      ],
      companies: [UGC_TABLE_KEY],
      companies_from_contacts: [CONTACTS_TABLE_KEY],
      contacts: [CONTACTS_TABLE_KEY],
      deals: [DEAL_TABLE_KEY],
      universities: [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
      k12: [K12_TABLE_KEY],
    }

    if (type in queryKeysMap) {
      queryKeysMap[type].forEach((key) => {
        queryClient.invalidateQueries([key])
      })
    }

    return res
  }

  async function bulkEditDistributors(
    type: 'doors' | 'chains' | 'companies' | 'deals' | 'universities' | 'k12',
    values: bulkEditDistributorsParams,
    subCategoryId?: number
  ) {
    const urlMap = {
      doors: 'restaurants/places/bulk/distributors/',
      chains: 'restaurants/chains/bulk/distributors/',
      companies: `contacts/contactcompanies/bulk/distributors/`,
      deals: 'contacts/deals/bulk/distributors/',
      universities: 'universities/bulk/distributors/',
      k12: 'k12/districts/bulk/distributors/',
    }
    const url = urlMap[type]

    const { params, ...rest } = values
    const queryParams = params ?? {}
    if (type === 'companies') {
      queryParams['sub_category_id'] = subCategoryId
    }

    const [err, res] = await to(
      clientInstance.post(url, rest, {
        params: queryParams,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )

    if (err) {
      notifyError(err.message)
      throw err
    }

    // Invalidate corresponding queries
    const queryKeysMap: Record<string, string[]> = {
      doors: [
        CHAINS_TABLE_KEY,
        DOORS_TABLE_KEY,
        DOOR_SUMMARY_KEY,
        CHAIN_SUMMARY_KEY,
      ],
      chains: [
        CHAINS_TABLE_KEY,
        DOORS_TABLE_KEY,
        DOOR_SUMMARY_KEY,
        CHAIN_SUMMARY_KEY,
      ],
      companies: [UGC_TABLE_KEY],
      deals: [DEAL_TABLE_KEY],
      universities: [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
      k12: [K12_TABLE_KEY],
    }
    if (type in queryKeysMap) {
      queryKeysMap[type].forEach((key) => {
        queryClient.invalidateQueries([key])
      })
    }

    return res
  }

  async function getProducts() {
    return baseAPICall<Product[]>(() => clientInstance.get('products/'))
  }

  const useGetProducts = () =>
    useQuery(['products'], getProducts, { refetchOnMount: false })

  async function getProductsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: Product
      }[]
    >(() =>
      clientInstance.get('products/', {
        transformResponse: (data) => {
          const products = JSON.parse(data)
          const opts = products.map((product: Product) => ({
            label: product.name,
            value: product.id,
            original: product,
          }))

          if (withNoneOption) {
            opts.unshift({ label: 'No Product', value: -1 })
          }

          return opts
        },
      })
    )
  }

  const useGetProductsOptions = (withNoneOption?: boolean) =>
    useQuery(['productsOptions', withNoneOption ? 'with-none' : ''], () =>
      getProductsOptions(withNoneOption)
    )

  async function postProduct(values: Omit<Product, 'id'>) {
    delete values.company

    return baseAPICall<Product>(
      () => clientInstance.post('products/', values),
      { successMessage: 'Product created successfully' }
    )
  }

  async function putProduct(id: number, values: Omit<Product, 'id'>) {
    delete values.company

    return baseAPICall<Product>(
      () => clientInstance.put(`products/${id}/`, values),
      { successMessage: 'Product updated successfully' }
    )
  }

  async function deleteProduct(id: number) {
    return baseAPICall(() => clientInstance.delete(`products/${id}/`), {
      successMessage: 'Product deleted successfully',
    })
  }

  async function getRestaurants(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('restaurants/places/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getRestaurantsLTVByProduct(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('restaurants/places/ltv-by-product', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getPlacesSummary(params = {}, signal?: AbortSignal) {
    return baseAPICall<PlacesSummary>(() =>
      clientInstance.get('restaurants/places/summary/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  async function getPlacesSummaryLtvByProduct(
    params = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PlacesSummary>(() =>
      clientInstance.get('restaurants/places/summary/ltv-by-product', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  const useGetPlacesSummary = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: Record<string, any> = {},
    product_ids: number[] = [],
    signal?: AbortSignal,
    enabled: boolean = true
  ) =>
    useQuery(
      [DOOR_SUMMARY_KEY, params, product_ids],
      () => {
        if (!product_ids.includes(-1)) {
          return getPlacesSummaryLtvByProduct(
            { ...params, product_id: product_ids[0] },
            signal
          )
        } else {
          return getPlacesSummary(
            { ...params, product_id: product_ids[0] },
            signal
          )
        }
      },
      { refetchOnMount: false, enabled: enabled }
    )

  async function getChainsSummary(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainsSummary>(() =>
      clientInstance.get('restaurants/chains/summary/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  async function getChainsSummaryLTVByProduct(
    params = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<ChainsSummary>(() =>
      clientInstance.get('restaurants/chains/summary/ltv-by-product', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  const useGetChainsSummary = (
    params = {},
    product_ids: number[] = [],
    signal?: AbortSignal
  ) =>
    useQuery([CHAIN_SUMMARY_KEY, params, product_ids], () => {
      if (!product_ids.includes(-1)) {
        return getChainsSummaryLTVByProduct(
          { ...params, product_id: product_ids[0] },
          signal
        )
      } else {
        return getChainsSummary(
          { ...params, product_id: product_ids[0] },
          signal
        )
      }
    })

  async function getPlace(placeId?: string): Promise<Restaurant> {
    if (!placeId) {
      notifyError('no place provided')
      throw new Error('no place provided')
    }
    const url = `restaurants/places/${placeId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetPlace = (placeId?: string) =>
    useQuery<Restaurant, AxiosError>(
      ['place', placeId],
      () => getPlace(placeId),
      {
        enabled: !!placeId,
        retry: (failureCount, error: Error | AxiosError) => {
          return axios.isAxiosError(error) && error?.response?.status === 404
            ? false
            : failureCount < 3
        },
      }
    )

  type ChainDetail = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    distributors: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    taglist: any[]

    instagram: {
      followers: number
      following: number
      posts: number
      url: string
    }

    total_ltv_sum: number
    revenue_ltv_sum: number
    brand_ltv_sum: number

    domain: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logo: any
    pop_density: number
    median_hhi: number
    reviews_per_score: number
    rating: number
    reviews_count: number
    cuisine_50: string
    expense_category: string
    count: number
    website: string
    description: string
    chain: string
    id: number
    contact_company_id?: number
  }

  async function getChain(chainId?: number): Promise<ChainDetail> {
    if (!chainId) {
      notifyError('no chain provided')
      throw new Error('no chain provided')
    }

    const url = `restaurants/chains/${chainId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getUniversity(
    universityId?: number
  ): Promise<UniversityChainProxy> {
    if (!universityId) {
      notifyError('no university provided')
      throw new Error('no university provided')
    }

    const url = `universities/${universityId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getUniversitiesList(params = {}) {
    return baseAPICall<PaginatedResponse<UniversityChainProxy>>(() =>
      clientInstance.get(`/universities/`, {
        params: {
          ...params,
        },
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  const useGetChain = (chainId?: number) =>
    useQuery<ChainDetail, AxiosError>(
      ['chain', chainId],
      () => getChain(chainId),
      {
        enabled: !!chainId, // Only fetch when chainId is truthy
        retry: (failureCount, error: Error | AxiosError) => {
          // By defualt, useQuery performs 3 retries on network errors
          // No retry on 404 error; continue on other errors up to 3 times
          return axios.isAxiosError(error) && error?.response?.status === 404
            ? false
            : failureCount < 3
        },
      }
    )

  async function getUsers() {
    return baseAPICall<User[]>(() => clientInstance.get('users/'))
  }

  const useGetUniversity = (universityId?: number) =>
    useQuery(['university', universityId], () => getUniversity(universityId), {
      enabled: !!universityId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By defualt, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  const useGetUsers = (disabled?: boolean) =>
    useQuery(['users'], getUsers, {
      enabled: !disabled,
      refetchOnMount: false,
    })

  const useGetUserOptions = (withNoneOption?: boolean) => {
    const { data: users } = useGetUsers()
    const opts = useMemo(() => {
      if (!users) return
      const out:
        | {
            label: string
            value: number
            original?: User
          }[]
        | undefined = users.map((user: User) => ({
        label: user.first_name + ' ' + user.last_name,
        value: user.id,
        original: user,
      }))

      if (withNoneOption) {
        out.unshift({ label: 'No User', value: -1 })
      }

      return out
    }, [users, withNoneOption])

    return {
      data: opts,
      isLoading: !users,
      isError: false,
    }
  }

  async function bulkEditCompanyAttrs(values: {
    chain_ids?: number[]
    contact_company_ids?: number[]
    taglist?: 'REMOVE' | number[]
    distributors?: 'REMOVE' | number[]
    domain?: string
    previous_taglist?: number[]
    previous_distributors?: number[]
  }) {
    const url = 'contacts/companyattrs-bulk/'

    const [err, res] = await to(clientInstance.post(url, values))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function bulkEditDeal(
    values: {
      account_owner?: number | null
      sales_stage?: number | null
      monthly_revenue_override?: number | null
      monthly_volume_override?: number | null
      close_date_override?: string | null
    } & Omit<bulkEditSingleParams, 'item_id'>
  ) {
    const { params, ...rest } = values

    const [err, res] = await to(
      clientInstance.post('contacts/deals/bulk/', rest, {
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function doorsBulkEditTags(values: bulkEditTagsParams) {
    return bulkEditTags('doors', values)
  }

  async function chainsBulkEditTags(values: bulkEditTagsParams) {
    return bulkEditTags('chains', values)
  }

  async function companiesBulkEditTags(
    subCategoryId: number,
    values: bulkEditTagsParams
  ) {
    return bulkEditTags('companies', values, subCategoryId)
  }

  async function dealsBulkEditTags(values: bulkEditTagsParams) {
    return bulkEditTags('deals', values)
  }

  async function contactsBulkEditContactTags(values: bulkEditTagsParams) {
    return bulkEditTags('contacts', values)
  }

  async function bulkTagsFromContacts(values: bulkEditTagsParams) {
    return bulkEditTags('companies_from_contacts', values)
  }

  async function universitiesBulkEditTags(values: bulkEditTagsParams) {
    return bulkEditTags('universities', values)
  }

  async function doorsBulkEditDistributors(values: bulkEditDistributorsParams) {
    return bulkEditDistributors('doors', values)
  }

  async function chainsBulkEditDistributors(
    values: bulkEditDistributorsParams
  ) {
    return bulkEditDistributors('chains', values)
  }

  async function companiesBulkEditDistributors(
    subCategoryId: number,
    values: bulkEditDistributorsParams
  ) {
    return bulkEditDistributors('companies', values, subCategoryId)
  }

  async function dealsBulkEditDistributors(values: bulkEditDistributorsParams) {
    return bulkEditDistributors('deals', values)
  }

  async function universitiesBulkEditDistributors(
    values: bulkEditDistributorsParams
  ) {
    return bulkEditDistributors('universities', values)
  }

  async function getSalesPipeline(params = {}, signal?: AbortSignal) {
    return baseAPICall<SalesPipeline>(() =>
      clientInstance.get('products/sales_pipeline_v2/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getPipeline(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('products/pipeline/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getSalesStages(signal?: AbortSignal) {
    return baseAPICall<SalesStage[]>(() =>
      clientInstance.get('products/sales_stages/', {
        signal,
      })
    )
  }

  const useGetSalesStages = (disabled?: boolean) =>
    useQuery(['stages'], () => getSalesStages(), {
      refetchOnMount: false,
      enabled: !disabled,
    })

  const useGetSalesStageOptions = (withNoneOption?: boolean) => {
    const { data } = useGetSalesStages()
    const out = useMemo(() => {
      if (!data) return
      const out:
        | {
            label: string
            value: number
            color: string
            original?: SalesStage
          }[]
        | undefined = data.map((s: SalesStage) => ({
        label: s.stage,
        value: s.id,
        color: getStageColor(s.win_rate)[0],
        original: s,
      }))

      if (withNoneOption) {
        out?.unshift({
          label: 'No Stage',
          value: -1,
          color: '#fff',
        })
      }

      return out
    }, [data, withNoneOption])

    return {
      data: out,
      isLoading: !data,
      isError: false,
    }
  }

  async function getTags(tagParent: tagParent = 'products') {
    return baseAPICall<Tag[]>(() => clientInstance.get(`${tagParent}/tags/`))
  }

  async function getProductTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`products/tags-list/`, {
        params: params,
        signal,
      })
    )
  }

  async function getContactTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`contacts/tags-list/`, {
        params: params,
        signal,
      })
    )
  }

  const useGetTags = (tagParent: tagParent = 'products', disabled?: boolean) =>
    useQuery(['tags', tagParent], () => getTags(tagParent), {
      enabled: !disabled,
      refetchOnMount: false,
    })

  const useGetTagsOptions = (
    withNoneOption?: boolean,
    tagParent: tagParent = 'products'
  ) => {
    const { data: tags } = useGetTags(tagParent)
    const opts = useMemo(() => {
      const opts:
        | {
            label: string
            value: number | string
            original?: Tag
          }[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        | undefined = tags?.map((tag: any) => ({
        label: tag.tag,
        value: tag.id,
        original: tag,
      }))

      if (withNoneOption) {
        opts?.unshift({ label: 'No Tag', value: 'REMOVE' })
      }
      return opts
    }, [tags, withNoneOption])

    return {
      data: opts,
      isLoading: !tags,
      isError: false,
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function postSalesStage(data: any) {
    return baseAPICall(() =>
      clientInstance.post('products/sales_stages/', data)
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function postTag(data: any, tagParent: tagParent = 'products') {
    return baseAPICall(() =>
      clientInstance.post(`${tagParent}/tags/`, data)
    ).then((res) => {
      queryClient.invalidateQueries(['tags', tagParent])
      return res
    })
  }

  async function putTag(
    id: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: { tag: string; description: string; color: string },
    tagParent: tagParent = 'products'
  ) {
    return baseAPICall(() =>
      clientInstance.put(`${tagParent}/tags/${id}/`, data)
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function putStage(id: number, data: any) {
    return baseAPICall(() =>
      clientInstance.put(`products/sales_stages/${id}/`, data)
    )
  }

  async function deleteStage(id: number) {
    return baseAPICall(() =>
      clientInstance.delete(`products/sales_stages/${id}/`)
    )
  }

  async function deleteTag(id: number, tagParent: tagParent = 'products') {
    return baseAPICall(() => clientInstance.delete(`${tagParent}/tags/${id}/`))
  }

  async function getDealsPipeline(params = {}, signal?: AbortSignal) {
    return baseAPICall<DealsPipeline[]>(() =>
      clientInstance.get('products/deals-pipeline/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getChainsList(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainListResponse>(() =>
      clientInstance.get('restaurants/chains/list/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getChainsListLTVByProduct(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainListResponse>(() =>
      clientInstance.get('restaurants/chains/list/ltv-by-product', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getChainMenuList(
    chainId?: number,
    params = {},
    signal?: AbortSignal
  ) {
    if (!chainId) {
      return [] as Menu[]
    }
    return baseAPICall<Menu[]>(() =>
      clientInstance.get(`restaurants/chains/${chainId}/menu/`, {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  const useGetChainMenuList = (chainId?: number, params = {}) =>
    useQuery(
      ['chain_menu_list', chainId, params],
      () => getChainMenuList(chainId, params),
      {
        refetchOnMount: false,
        enabled: !!chainId,
      }
    )

  async function getDoorsMenuList(
    doorId?: number,
    params = {},
    signal?: AbortSignal
  ) {
    if (!doorId) {
      return [] as Menu[]
    }
    return baseAPICall<Menu[]>(() =>
      clientInstance.get(`restaurants/places/${doorId}/menu/`, {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  const useGetDoorsMenuList = (doorId?: number, params = {}) =>
    useQuery(
      ['doors_menu_list', doorId, params],
      () => getDoorsMenuList(doorId, params),
      { refetchOnMount: false, enabled: !!doorId }
    )

  async function getContactsList(
    params = {},
    signal?: AbortSignal
  ): Promise<ContactListResponse> {
    return baseAPICall<ContactListResponse>(() =>
      clientInstance.get('contacts/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getMyContactsList(
    params = {},
    signal?: AbortSignal
  ): Promise<ContactListResponse> {
    return baseAPICall<ContactListResponse>(() =>
      clientInstance.get('contacts/my/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getActivityList(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponse> {
    return baseAPICall<ActivityListResponse>(() =>
      clientInstance.get('activity/team/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getMyActivityList(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponse> {
    return baseAPICall<ActivityListResponse>(() =>
      clientInstance.get('activity/my/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getActivityListV2(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/team/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getMyActivityListV2(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/my/', {
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function searchCompaniesList(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any = {},
    signal?: AbortSignal
  ): Promise<ContactCompanySearchResult[]> {
    return baseAPICall(() =>
      clientInstance.get('contacts/companies-search/', {
        signal,
        params,
      })
    )
  }

  async function searchCompaniesUniqueDomain(
    domain: string,
    signal?: AbortSignal
  ): Promise<ContactCompanySearchResult[]> {
    return baseAPICall(() =>
      clientInstance.get('contacts/companies-search/domain', {
        signal,
        params: { domain: domain },
      })
    )
  }

  async function getCompanyCategoriesAndSub() {
    type CompanyCategoriesAndSubcategories = {
      id: number
      name: string
      slug: string
      description: string
      sub_categories: {
        id: number
        name: string
        slug: string
        description: string
      }[]
    }[]

    const res = await baseAPICall<CompanyCategoriesAndSubcategories>(() =>
      clientInstance.get('contacts/companies-by-category/')
    )

    res.sort((a, b) => a.name.charCodeAt(0) - b.name.charCodeAt(0))

    return res
  }

  const useGetCompanyCategoriesAndSub = (disabled = false) =>
    useQuery(['companyCategoriesAndSub'], () => getCompanyCategoriesAndSub(), {
      enabled: !disabled,
      refetchOnMount: false,
    })

  async function createContact(data: ContactCreateRequest) {
    return baseAPICall(() => clientInstance.post('contacts/', data))
  }

  async function updateContact(id: number, data: ContactCreateRequest) {
    return baseAPICall(() => clientInstance.patch(`contacts/${id}/`, data))
  }

  async function deleteContact(id: number) {
    return baseAPICall(() => clientInstance.delete(`contacts/${id}/`))
  }

  async function downloadDoorsCsv(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'restaurants/places/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function downloadDoorsCsvByProduct(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'restaurants/places/export/ltv-by-product',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function downloadChainsCsv(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'restaurants/chains/export/',

        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function downloadChainsCsvByProduct(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'restaurants/chains/export/ltv-by-product',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function exportContactCompanies(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'contacts/contactcompanies/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function exportDeals(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'contacts/deals/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function exportUniversities(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'universities/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function exportContacts(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'contacts/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function exportDealsPipeline(
    params = {},
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx',
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.post(
        'products/deals-pipeline/export/',
        {
          selected_columns: selectedColumns,
          export_type: exportType,
        },
        {
          responseType: 'blob',
          signal,
          params,
          paramsSerializer: (p) => {
            return qs.stringify(p)
          },
        }
      )
    )
  }

  async function downloadActivityExport(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('activity/team/export/', {
        responseType: 'blob',
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function downloadMyActivityExport(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('activity/my/export/', {
        responseType: 'blob',
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function downloadActivityExportV2(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('activity/v2/team/export/', {
        responseType: 'blob',
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function downloadMyActivityExportV2(params = {}, signal?: AbortSignal) {
    return baseAPICall(() =>
      clientInstance.get('activity/v2/my/export/', {
        responseType: 'blob',
        signal,
        params,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  function getContactsImportFormData(
    file: File,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    column_mapping: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    company_mapping: any = null,
    tags: number[] = []
  ): FormData {
    const formData = new FormData()
    formData.append('file', file)
    const jsonBlob = new Blob([JSON.stringify(column_mapping)], {
      type: 'application/json',
    })
    formData.append('column_mapping', jsonBlob)
    if (company_mapping) {
      const jsonBlob = new Blob([JSON.stringify(company_mapping)], {
        type: 'application/json',
      })
      formData.append('company_mapping', jsonBlob)
    }
    if (tags.length > 0) {
      const jsonBlob = new Blob([JSON.stringify(tags)], {
        type: 'application/json',
      })
      formData.append('tags', jsonBlob)
    }
    return formData
  }

  async function previewContactsImport(
    file: File,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    column_mapping: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    company_mapping: any = null
  ): Promise<ContactsImportResponse> {
    const formData = getContactsImportFormData(
      file,
      column_mapping,
      company_mapping
    )
    return baseAPICall(() =>
      clientInstance.post('contacts/import/preview/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function contactsImport(
    file: File,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    column_mapping: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    company_mapping: any = null,
    tags: number[] = []
  ): Promise<ContactsImportResponse> {
    const formData = getContactsImportFormData(
      file,
      column_mapping,
      company_mapping,
      tags
    )
    return baseAPICall(() =>
      clientInstance.post('contacts/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function getCompanyCategories() {
    return baseAPICall<{
      company_categories: Category[]
      contact_types: ContactType[]
    }>(() => clientInstance.get('contacts/categories/'))
  }

  const useGetCompanyCategories = (enabled = true) =>
    useQuery(['company_categories'], getCompanyCategories, {
      enabled: enabled,
      refetchOnMount: false,
    })

  async function getCompaniesList(subCategory: number, params = {}) {
    return baseAPICall<PaginatedResponse<IContactCompany>>(() =>
      clientInstance.get(`/contacts/contactcompanies/`, {
        params: {
          sub_category_id: subCategory,
          ...params,
        },
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function updateCompaniesNotes(body: {
    contact_company_ids: number[]
    taglist: number[]
    notes: string
  }) {
    return baseAPICall(() => clientInstance.put(`/notes`, body))
  }

  async function getCompany(id: number): Promise<IContactCompany> {
    return baseAPICall(() =>
      clientInstance.get(`contacts/contactcompanies/${id}/`)
    )
  }

  const useGetCompany = (companyId: number) =>
    useQuery<IContactCompany, AxiosError>(
      ['company', companyId],
      () => getCompany(companyId),
      {
        enabled: !!companyId,
        retry: (failureCount, error: AxiosError) => {
          return axios.isAxiosError(error) && error?.response?.status === 404
            ? false
            : failureCount < 3
        },
      }
    )

  async function createCompany(
    data: IContactCompanyCreateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(
      () => clientInstance.post(`contacts/contactcompanies/create/`, data),
      { disableNotifyGenericError: true }
    )
  }

  async function createDoorProxy(
    data: IDoorProxyCreateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(() =>
      clientInstance.post(`contacts/doorproxies/create/`, data)
    )
  }

  async function editDoorProxy(
    data: IDoorProxyEditRequest,
    doorProxyId: number
  ): Promise<IDoorProxy> {
    return baseAPICall<IDoorProxy>(() =>
      clientInstance.patch(`contacts/doorproxies/${doorProxyId}/edit/`, data)
    )
  }

  async function updateCompany(
    id: number,
    data: IContactCompanyUpdateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/contactcompanies/edit/${id}/`, data)
    )
  }

  async function getCompanyDistributorsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: CompanyDistributor
      }[]
    >(() =>
      clientInstance.get('contacts/companies/distributors/', {
        transformResponse: (data) => {
          const distributors = JSON.parse(data)
          const opts = distributors.map((distributor: CompanyDistributor) => ({
            label: distributor.name,
            value: distributor.id,
            original: distributor,
          }))

          if (withNoneOption) {
            opts.unshift({ label: 'No Distributor', value: -1 })
          }

          return opts
        },
      })
    )
  }

  async function getUniversityFoodManagementCompanyOptions(
    withNoneOption?: boolean
  ) {
    return baseAPICall<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      food_management_companies: any[]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      parent_food_management_companies: any[]
    }>(() =>
      clientInstance.get('universities/food_management_companies', {
        transformResponse: (data) => {
          const companies = JSON.parse(data)
          const options = {
            food_management_companies: [],
            parent_food_management_companies: [],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any

          companies.food_management_companies.forEach((company: string) => {
            options.food_management_companies.push({
              label: company,
              value: company,
            })
          })

          companies.parent_food_management_companies.forEach(
            (parentCompany: string) => {
              options.parent_food_management_companies.push({
                label: parentCompany,
                value: parentCompany,
              })
            }
          )

          if (withNoneOption) {
            options.food_management_companies.unshift({
              label: 'No Company',
              value: '',
            })
            options.parent_food_management_companies.unshift({
              label: 'No Company',
              value: '',
            })
          }

          return options
        },
      })
    )
  }

  const useGetDistributors = () =>
    useQuery(['base-distributors'], () => getDistributors(), {
      refetchOnMount: false,
    })

  const useGetCompanyDistributorsOptions = (withNoneOption?: boolean) =>
    useQuery(
      ['distributors', withNoneOption ? 'with-none' : ''],
      () => getCompanyDistributorsOptions(withNoneOption),
      {
        refetchOnMount: false,
      }
    )

  const useGetUniversityFoodManagementCompanyOptions = (
    withNoneOption?: boolean
  ) =>
    useQuery(
      ['food_management_companies', withNoneOption ? 'with-none' : ''],
      () => getUniversityFoodManagementCompanyOptions(withNoneOption),
      {
        refetchOnMount: false,
      }
    )

  const postDistributor = async (data: CompanyDistributorCreate) => {
    return baseAPICall(() =>
      clientInstance.post(`contacts/companies/distributors/`, data)
    )
  }

  const updateDistributor = async (
    id: number,
    data: Partial<CompanyDistributorCreate>
  ) => {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/companies/distributors/${id}/`, data)
    )
  }

  const deleteDistributor = async (id: number) => {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/companies/distributors/${id}/`)
    )
  }

  async function getDistributors({
    signal,
    params = {},
  }: { signal?: AbortSignal; params?: Record<string, unknown> } = {}) {
    return baseAPICall<CompanyDistributor[]>(() =>
      clientInstance.get('contacts/companies/distributors/', {
        params,
        signal,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  const getProductPredictions = async ({
    companyId,
    chainId,
  }: {
    companyId?: number
    chainId?: number
  }): Promise<ProductPrediction[]> => {
    return baseAPICall<ProductPrediction[]>(() =>
      clientInstance.get(`products/predictions/`, {
        params: {
          company_id: companyId,
          chain_id: chainId,
        },
      })
    )
  }

  const useGetProductPredictions = ({
    companyId,
    chainId,
    enabled = true,
  }: {
    companyId?: number
    chainId?: number
    enabled?: boolean
  }) =>
    useQuery(
      ['product_predictions', companyId, chainId],
      () =>
        getProductPredictions({
          companyId,
          chainId,
        }),
      {
        enabled: enabled && (!!companyId || !!chainId),
      }
    )

  async function getDeals(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<Deal>>(() =>
      clientInstance.get(`contacts/deals/`, {
        params,
        signal,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function getNotes(
    chain_id: number | undefined,
    contact_company_id: number | undefined,
    params = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<INote>>(() =>
      clientInstance.get(`contacts/contactcompanies/notes/`, {
        params: {
          ...params,
          chain_id: chain_id,
          contact_company_id: contact_company_id,
        },
        signal,
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  async function editNote(id: number, content: string) {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/contactcompanies/notes/edit/${id}/`, {
        content: content,
      })
    )
  }

  async function createNote(
    chain_id: number | undefined,
    contact_company_id: number | undefined,
    content: string
  ): Promise<INote> {
    return baseAPICall(() =>
      clientInstance.post(`contacts/contactcompanies/notes/create/`, {
        chain_id,
        contact_company_id,
        content,
      })
    )
  }

  async function deleteNote(id: number) {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/contactcompanies/notes/delete/${id}/`)
    )
  }

  const useGetDeals = (params = {}) =>
    useQuery(['deals', JSON.stringify(params)], () => getDeals(params))

  const dealsBulk = async (data: {
    chain_ids?: number[]
    contact_company_ids?: number[]
    deals: Partial<DealCreate>[]
  }) => {
    return baseAPICall(() =>
      clientInstance.post(`contacts/deals/create/`, data)
    )
  }

  const editDeal = async (data: DealEdit) => {
    return baseAPICall(() => clientInstance.post(`contacts/deals/edit/`, data))
  }

  async function deleteDeal(id: number) {
    return baseAPICall(() => clientInstance.delete(`contacts/deals/${id}/`))
  }

  const useProducts = (disabled?: boolean) =>
    useQuery(
      ['product'],
      async () => {
        return await getProducts()
      },
      {
        enabled: !disabled,
      }
    )

  const deleteCompany = async (id: number) => {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/contactcompanies/delete/${id}/`)
    )
  }

  const getDealHistory = async (dealId: number): Promise<DealHistory[]> => {
    return baseAPICall<DealHistory[]>(() =>
      clientInstance.get(`contacts/deals/history/${dealId}/`)
    )
  }

  const useGetDealHistory = ({ dealId }: { dealId: number }) => {
    return useQuery(['deal_history', dealId], () => getDealHistory(dealId))
  }

  async function getPreferences() {
    return baseAPICall<Preferences>(() => clientInstance.get('preferences/'))
  }

  const useGetPreferences = (disabled?: boolean) =>
    useQuery(['preferences'], getPreferences, {
      refetchOnMount: false,
      enabled: !disabled,
    })

  async function getUnreadNotificationsCount() {
    return baseAPICall<{
      count: number
    }>(() => clientInstance.get('notifications/unread-count/'))
  }

  const useGetUnreadNotificationsCount = () =>
    useQuery(['unread-notifications'], getUnreadNotificationsCount, {
      refetchOnMount: true,
      refetchInterval: 10000,
    })

  async function getNotifications({ unread }: { unread?: boolean } = {}) {
    return baseAPICall<Notification[]>(() =>
      clientInstance.get('notifications/', {
        params: {
          is_read: !unread,
        },
        paramsSerializer: (p) => {
          return qs.stringify(p)
        },
      })
    )
  }

  const useGetNotifications = ({ unread }: { unread?: boolean } = {}) =>
    useQuery(['notifications', unread], () => getNotifications({ unread }), {
      refetchOnMount: true,
    })

  async function markNotificationAsRead(ids: number[]) {
    return baseAPICall<Notification>(() =>
      clientInstance.post(`notifications/mark-as-read/`, {
        notification_ids: ids,
      })
    )
  }

  async function markAllNotificationsAsRead() {
    return baseAPICall<Notification>(() =>
      clientInstance.post(`notifications/mark-all-as-read/`)
    )
  }

  function getDashboardOverview() {
    return baseAPICall(() =>
      clientInstance.get('products/dashboard/overview/')
    ) as Promise<
      {
        title: string
        value: string
        tooltip: string
      }[]
    >
  }

  const useGetDashboardOverview = () => {
    return useQuery(['dashboard_overview'], getDashboardOverview)
  }

  function getDashboardSalesForecast(
    timeHorizon = 12,
    accountOwnerId?: number,
    productId?: number,
    stageId?: number,
    subCatId?: number | null
  ) {
    return baseAPICall(() =>
      clientInstance.get('products/dashboard/sales_forecast/', {
        params: {
          time_horizon: timeHorizon,
          account_owner_id: accountOwnerId,
          product_id: productId,
          stage_id: stageId,
          sub_category_id: subCatId,
        },
      })
    ) as Promise<{
      charts: {
        [key: string]: {
          labels: string[]
          values: number[]
        }
      }
      cards: { label: string; value: string; tooltip?: string }[]
    }>
  }

  const useGetDashboardSalesForecast = (
    timeHorizon = 12,
    accountOwnerId?: number,
    productId?: number,
    stageId?: number,
    subCatId?: number | null
  ) => {
    return useQuery(
      [
        'sales_forecast',
        timeHorizon,
        accountOwnerId,
        productId,
        stageId,
        subCatId,
      ],
      () =>
        getDashboardSalesForecast(
          timeHorizon,
          accountOwnerId,
          productId,
          stageId,
          subCatId
        )
    )
  }

  async function saveFilters(payload: SavedViewCreate) {
    return baseAPICall<Notification[]>(
      () =>
        clientInstance.post('saved-views/', {
          ...payload,
        }),
      { disableNotifyError: true }
    )
  }

  async function getSavedFiltersOptions(identifier: SavedViewIdentifier) {
    return baseAPICall<
      {
        label: string
        value: number
        original: SavedView
      }[]
    >(() =>
      clientInstance.get('saved-views/', {
        params: {
          identifier,
        },
        transformResponse: (data) => {
          const savedViews = JSON.parse(data)
          return savedViews.map((savedView: SavedView) => ({
            label: savedView.name,
            value: savedView.id,
            original: savedView,
          }))
        },
      })
    )
  }

  const useGetSavedFiltersOptions = (identifier: SavedViewIdentifier) => {
    return useQuery(['saved_filters', identifier], () =>
      getSavedFiltersOptions(identifier)
    )
  }

  const postSuggestion = async (data: SuggestionCreate) => {
    return baseAPICall(() =>
      clientInstance.post('restaurants/suggestions/', data)
    )
  }

  async function getDoorProxiesOfChainProxy(chainProxyId?: number) {
    if (!chainProxyId) {
      return null
    }

    return baseAPICall<IDoorProxyListResponse>(() =>
      clientInstance.get(`contacts/contactcompanies/${chainProxyId}/doors/`)
    )
  }

  const useDoorProxiesOfChainProxy = (chainProxyId?: number) =>
    useQuery(
      ['door_proxies_of_chain_proxy', chainProxyId],
      () => getDoorProxiesOfChainProxy(chainProxyId),
      {
        refetchOnMount: false,
      }
    )

  function deleteDoorProxy(doorProxyId: number) {
    return clientInstance.delete(`contacts/doorproxies/${doorProxyId}/delete/`)
  }

  async function getUnmatchedCompanyCities(
    subCategoryId: number,
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`contacts/contactcompanies/cities/`, {
        params: {
          sub_category_id: subCategoryId,
        },
        signal,
      })
    )
  }

  const useGetUnmatchedCompanyCities = (subCategoryId: number) =>
    useQuery(
      ['contact_company_cities', subCategoryId],
      () => getUnmatchedCompanyCities(subCategoryId),
      {
        refetchOnMount: false,
      }
    )

  return {
    postProduct,
    putProduct,
    deleteProduct,
    getRestaurants,
    getPlace,
    useGetPlace,
    getChain,
    getUsers,
    useGetUsers,
    bulkEditCompanyAttrs,
    doorsBulkEditTags,
    chainsBulkEditTags,
    companiesBulkEditTags,
    dealsBulkEditTags,
    doorsBulkEditDistributors,
    chainsBulkEditDistributors,
    companiesBulkEditDistributors,
    dealsBulkEditDistributors,
    getSalesPipeline,
    getSalesStages,
    getTags,
    postSalesStage,
    postTag,
    putTag,
    putStage,
    deleteStage,
    deleteTag,
    useGetSalesStages,
    useGetTags,
    getChainsList,
    useGetProducts,
    getPipeline,
    useGetTagsOptions,
    useGetUserOptions,
    useGetSalesStageOptions,
    useGetPlacesSummary,
    useGetChainsSummary,
    downloadDoorsCsv,
    downloadChainsCsv,
    getContactsList,
    searchCompaniesList,
    createContact,
    deleteContact,
    getMyContactsList,
    updateContact,
    previewContactsImport,
    contactsImport,
    contactsBulkEditContactTags,
    useGetCompanyCategories,
    useGetCompanyCategoriesAndSub,
    getCompaniesList,
    updateCompaniesNotes,
    getCompany,
    useGetCompany,
    createCompany,
    updateCompany,
    useGetCompanyDistributorsOptions,
    useGetUniversityFoodManagementCompanyOptions,
    useGetProductPredictions,
    getDeals,
    useGetDeals,
    dealsBulk,
    bulkEditDeal,
    deleteDeal,
    useProducts,
    editDeal,
    deleteCompany,
    useGetProductsOptions,
    useGetDealHistory,
    getRestaurantsLTVByProduct,
    getChainsListLTVByProduct,
    downloadDoorsCsvByProduct,
    downloadChainsCsvByProduct,
    exportDeals,
    exportContacts,
    downloadActivityExport,
    downloadMyActivityExport,
    getDealsPipeline,
    useGetPreferences,
    getActivityList,
    getMyActivityList,
    getNotes,
    editNote,
    deleteNote,
    createNote,
    useGetChain,
    useGetUnreadNotificationsCount,
    useGetNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    useGetDashboardOverview,
    saveFilters,
    useGetSavedFiltersOptions,
    useGetDashboardSalesForecast,
    getChainMenuList,
    useGetChainMenuList,
    useGetUniversity,
    getUniversitiesList,
    postSuggestion,
    getDoorsMenuList,
    useGetDoorsMenuList,
    getActivityListV2,
    exportDealsPipeline,
    postDistributor,
    updateDistributor,
    getDistributors,
    deleteDistributor,
    exportContactCompanies,
    useDoorProxiesOfChainProxy,
    createDoorProxy,
    deleteDoorProxy,
    editDoorProxy,
    universitiesBulkEditDistributors,
    universitiesBulkEditTags,
    exportUniversities,
    getK12DistrictsList,
    useGetK12District,
    k12BulkEditDistributors,
    k12BulkEditTags,
    exportK12Districts,
    useK12CountiesOptions,
    searchCompaniesUniqueDomain,
    getProductTagsList,
    getContactTagsList,
    getMyActivityListV2,
    bulkEditTags,
    bulkEditDistributors,
    useGetUnmatchedCompanyCities,
    useGetDistributors,
    downloadActivityExportV2,
    downloadMyActivityExportV2,
    bulkTagsFromContacts,
  }
}
